import { LOCATIONS } from "@/config/locations";
import { orgLocationPickerLabel } from "@/lib/locations/orgLocationPickerOptions";
import { formatMemberName, type MemberWithProfile, type OrgRole } from "./adminTypes";
import { setMemberAdsRunnerCapability } from "@/lib/capabilities/memberCapabilitiesClient";

type Params = {
  sb: any;
  effectiveOrgId: string | null | undefined;
  blockReasonByUser: Record<string, string>;
  memberPlatformRoleEdits: Record<string, string>;
  refresh: () => Promise<void>;
  formatAdminError: (err: unknown) => string;
  getRoleFor: (userId: string) => OrgRole;
  handleRoleError: (error: any, role: OrgRole) => never;
  setMsg: (value: string) => void;
  setLoading: (value: boolean) => void;
  setAddLocByUser: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  setAddPlantByUser?: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
};

export function useAdminPanelMemberActions(params: Params) {
  async function approveAndAssign(profileId: string) {
    if (!params.effectiveOrgId) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const role = params.getRoleFor(profileId);
      const { error: assignErr } = await params.sb.rpc("admin_assign_membership", {
        p_profile_id: profileId,
        p_org_id: params.effectiveOrgId,
        p_role: role,
      });
      if (assignErr) {
        const { error: approveErr } = await params.sb.rpc("admin_approve_profile", {
          p_profile_id: profileId,
          p_org_id: params.effectiveOrgId,
          p_role: role,
        });
        if (approveErr) params.handleRoleError(approveErr, role);
      }
      params.setMsg("Approved and role assigned.");
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function assignRole(profileId: string) {
    if (!params.effectiveOrgId) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const role = params.getRoleFor(profileId);
      const { error } = await params.sb.rpc("admin_assign_membership", {
        p_profile_id: profileId,
        p_org_id: params.effectiveOrgId,
        p_role: role,
      });
      if (error) params.handleRoleError(error, role);
      params.setMsg("Role assigned.");
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function block(profileId: string, name: string) {
    if (!params.effectiveOrgId) return;
    const reason = params.blockReasonByUser[profileId] ?? "";
    const confirmMsg = reason ? `Block "${name}"?\nReason: "${reason}"` : `Block "${name}"? They will lose access to this org.`;
    if (!window.confirm(confirmMsg)) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const { error } = await params.sb.rpc("admin_block_profile", {
        p_profile_id: profileId,
        p_org_id: params.effectiveOrgId,
        p_reason: reason,
        p_deactivate_memberships: true,
      });
      if (error) throw error;
      params.setMsg("User blocked.");
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function addOrgRole(member: MemberWithProfile, role: OrgRole) {
    if (!params.effectiveOrgId) return;
    const name = formatMemberName(member);
    if (!window.confirm(`Add role "${role}" to ${name}?`)) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const { error } = await params.sb.rpc("admin_add_org_role", {
        p_profile_id: member.user_id,
        p_org_id: params.effectiveOrgId,
        p_role: role,
      });
      if (error) throw error;
      params.setMsg(`Added role "${role}" to ${name}.`);
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function removeOrgRole(member: MemberWithProfile, role: OrgRole) {
    if (!params.effectiveOrgId) return;
    const name = formatMemberName(member);
    if (!window.confirm(`Remove role "${role}" from ${name}?`)) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const { error } = await params.sb.rpc("admin_remove_org_role", {
        p_profile_id: member.user_id,
        p_org_id: params.effectiveOrgId,
        p_role: role,
      });
      if (error) throw error;
      params.setMsg(`Removed role "${role}" from ${name}.`);
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function updateMemberPlatformRole(member: MemberWithProfile) {
    const newRole = params.memberPlatformRoleEdits[member.user_id] ?? "";
    const name = formatMemberName(member);
    const label = newRole || "none";
    if (!window.confirm(`Set ${name}'s platform role to "${label}"?`)) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const { error } = await params.sb.rpc("platform_set_user_platform_role", {
        p_user_id: member.user_id,
        p_role: newRole,
      });
      if (error) throw error;
      params.setMsg(`${name}'s platform role updated to "${label}".`);
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function deactivateMembership(member: MemberWithProfile) {
    if (!params.effectiveOrgId) return;
    const name = formatMemberName(member);
    if (!window.confirm(`Remove "${name}" from this org?\n\nTheir account stays active but they lose access here.`)) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const { error } = await params.sb.rpc("admin_deactivate_membership", {
        p_profile_id: member.user_id,
        p_org_id: params.effectiveOrgId,
      });
      if (error) throw error;
      params.setMsg(`${name} removed from org.`);
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function addUserLocation(member: MemberWithProfile, locId: string) {
    if (!params.effectiveOrgId || !locId) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const { error } = await params.sb.rpc("admin_add_user_location", {
        p_profile_id: member.user_id,
        p_org_id: params.effectiveOrgId,
        p_location_id: locId,
      });
      if (error) throw error;
      params.setAddLocByUser((prev) => ({ ...prev, [member.user_id]: "" }));
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function removeUserLocation(member: MemberWithProfile, locId: string) {
    if (!params.effectiveOrgId || !locId) return;
    const locLabel = orgLocationPickerLabel(
      LOCATIONS.map((l) => ({ id: l.id, label: l.fullName })),
      locId
    );
    if (!window.confirm(`Remove ${locLabel} from ${formatMemberName(member)}?`)) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const { error } = await params.sb.rpc("admin_remove_user_location", {
        p_profile_id: member.user_id,
        p_org_id: params.effectiveOrgId,
        p_location_id: locId,
      });
      if (error) throw error;
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function addUserPlant(member: MemberWithProfile, plantId: string) {
    if (!params.effectiveOrgId || !plantId) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const { error } = await params.sb.rpc("admin_add_user_plant", {
        p_profile_id: member.user_id,
        p_org_id: params.effectiveOrgId,
        p_plant_id: plantId,
      });
      if (error) throw error;
      params.setAddPlantByUser?.((prev) => ({ ...prev, [member.user_id]: "" }));
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function removeUserPlant(member: MemberWithProfile, plantId: string) {
    if (!params.effectiveOrgId || !plantId) return;
    if (!window.confirm(`Remove plant assignment from ${formatMemberName(member)}?`)) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const { error } = await params.sb.rpc("admin_remove_user_plant", {
        p_profile_id: member.user_id,
        p_org_id: params.effectiveOrgId,
        p_plant_id: plantId,
      });
      if (error) throw error;
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function setMemberAdsRunner(member: MemberWithProfile, value: boolean) {
    if (!params.effectiveOrgId) return;
    const name = formatMemberName(member);
    const verb = value ? "Grant" : "Revoke";
    if (!window.confirm(`${verb} ads-runner for ${name}?`)) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const result = await setMemberAdsRunnerCapability({
        organizationId: params.effectiveOrgId,
        userId: member.user_id,
        value,
      });
      if (!result.ok) throw new Error(result.error);
      params.setMsg(
        value ? `Granted ads-runner to ${name}.` : `Revoked ads-runner from ${name}.`,
      );
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  return {
    approveAndAssign,
    assignRole,
    block,
    addOrgRole,
    removeOrgRole,
    updateMemberPlatformRole,
    deactivateMembership,
    addUserLocation,
    removeUserLocation,
    addUserPlant,
    removeUserPlant,
    setMemberAdsRunner,
  };
}
